import React, { FC, KeyboardEvent, useState } from "react";
import { Select, Input, Tooltip } from "antd";
// No search or sort glyph exists in the app set, so those two stay on antd.
import { SearchOutlined, SortAscendingOutlined } from "@ant-design/icons";
import BeamIcon from "./BeamIcon";
import "./MessageTypes.scss";

/**
 * Global Page Filter & Sort Bar + the two-tier sorting control.
 *
 * Tier 1 is the type pill row — it buckets the page by message type and always
 * shows counts, so an empty bucket is obvious before you click it.
 *
 * Tier 2 only appears once a specific type is selected. Sorting "within a
 * bucket" has no meaning while All is active, and rendering a disabled control
 * there would just be noise.
 */

export interface TypeCount {
  key: string;
  label: string;
  /** Omitted when no total is available — the badge is hidden rather than guessed. */
  count?: number;
}

export type PrimarySort = "newest" | "oldest" | "severity" | "author";
export type SecondarySort = "author" | "recipient" | "alphabetical";

export interface FilterState {
  type: string;
  primarySort: PrimarySort;
  secondarySort: SecondarySort;
  query: string;
  tags: string[];
}

interface GlobalFilterBarProps {
  typeCounts: TypeCount[];
  suggestedTags?: string[];
  /**
   * Sorts the backing index cannot serve. They stay visible but disabled with
   * the reason attached — hiding them would make the page look like it never
   * offered the option, and enabling them would silently do nothing.
   */
  unavailableSorts?: PrimarySort[];
  /**
   * Set when the host screen already owns a search box and a narrow column.
   * The bar then drops its own search input rather than showing a second one
   * beside it, and keeps the pills on a single scrolling row so the stream
   * underneath is not pushed off screen.
   */
  compact?: boolean;
  /**
   * Active bucket when the host owns the filter. The screen this bar lives on
   * can also be filtered from its own funnel popup, so the pill has to follow
   * that state rather than only its own clicks.
   */
  activeType?: string;
  /**
   * Active sorts when the host owns them. The bar is mounted by two panels that
   * swap places as the filter changes, so it remounts mid-session — reading the
   * sort back from the host keeps the control showing the sort the query is
   * actually running under instead of resetting to the default.
   */
  activePrimarySort?: PrimarySort;
  activeSecondarySort?: SecondarySort;
  onChange?: (state: FilterState) => void;
}

const PRIMARY_SORTS: { value: PrimarySort; label: string }[] = [
  { value: "newest", label: "Newest first" },
  { value: "oldest", label: "Oldest first" },
  { value: "severity", label: "Priority / Severity" },
  { value: "author", label: "Author / Source" },
];

const SECONDARY_SORTS: { value: SecondarySort; label: string }[] = [
  { value: "author", label: "By author" },
  { value: "recipient", label: "By recipient" },
  { value: "alphabetical", label: "Alphabetical" },
];

const GlobalFilterBar: FC<GlobalFilterBarProps> = ({
  typeCounts,
  suggestedTags = [],
  unavailableSorts = [],
  compact = false,
  activeType,
  activePrimarySort,
  activeSecondarySort,
  onChange,
}) => {
  const [state, setState] = useState<FilterState>({
    type: "all",
    primarySort: "newest",
    secondarySort: "author",
    query: "",
    tags: [],
  });

  const update = (patch: Partial<FilterState>) => {
    // Host-owned fields win over the local copy, otherwise a click on one
    // control would report — and so write back — a stale value for the others.
    const next = {
      ...state,
      type: activeType ?? state.type,
      primarySort: activePrimarySort ?? state.primarySort,
      secondarySort: activeSecondarySort ?? state.secondarySort,
      ...patch,
    };
    setState(next);
    onChange?.(next);
  };

  /** A leading "#" in the search box promotes the term to a tag pill. */
  const handleSearchKey = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key !== "Enter") return;
    const raw = state.query.trim();
    if (!raw.startsWith("#") || raw.length < 2) return;
    const tag = raw.slice(1);
    if (state.tags.includes(tag)) {
      update({ query: "" });
      return;
    }
    update({ tags: [...state.tags, tag], query: "" });
  };

  const removeTag = (tag: string) =>
    update({ tags: state.tags.filter((t) => t !== tag) });

  const addTag = (tag: string) => {
    if (state.tags.includes(tag)) return;
    update({ tags: [...state.tags, tag] });
  };

  const selectedType = activeType ?? state.type;
  const selectedPrimarySort = activePrimarySort ?? state.primarySort;
  const selectedSecondarySort = activeSecondarySort ?? state.secondarySort;

  const hasActiveFilters =
    state.type !== "all" || state.tags.length > 0 || !!state.query;

  return (
    <div className={`mt-filterbar ${compact ? "is-compact" : ""}`}>
      {/* ---- tier 1: type buckets ---- */}
      <div className="mt-filterbar__types">
        {typeCounts.map((item) => (
          <button
            key={item.key}
            type="button"
            className={`mt-filterbar__pill ${
              selectedType === item.key ? "is-active" : ""
            } ${item.count === 0 ? "is-empty" : ""}`}
            onClick={() => update({ type: item.key })}
          >
            {item.label}
            {typeof item.count === "number" && (
              <span className="mt-filterbar__pill-count">{item.count}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-filterbar__controls">
        {!compact && (
        <div className="mt-filterbar__search">
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Search text, or type #tag and press Enter"
            value={state.query}
            onChange={(e) => update({ query: e.target.value })}
            onKeyDown={handleSearchKey}
          />
          {state.tags.length > 0 && (
            <div className="mt-filterbar__tags">
              {state.tags.map((tag) => (
                <span key={tag} className="mt-filterbar__tag">
                  #{tag}
                  <button
                    type="button"
                    onClick={() => removeTag(tag)}
                    aria-label={`Remove ${tag}`}
                  >
                    <BeamIcon name="close" size={10} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>
        )}

        <Tooltip title="Sort the whole page">
          <Select<PrimarySort>
            className="mt-filterbar__sort"
            value={selectedPrimarySort}
            onChange={(value) => update({ primarySort: value })}
            options={PRIMARY_SORTS.map((option) =>
              unavailableSorts.includes(option.value)
                ? {
                    ...option,
                    disabled: true,
                    label: `${option.label} — not indexed`,
                  }
                : option
            )}
            suffixIcon={<SortAscendingOutlined />}
            dropdownMatchSelectWidth={false}
          />
        </Tooltip>
      </div>

      {/* ---- tier 2: sort within the selected bucket ---- */}
      {selectedType !== "all" && (
        <div className="mt-filterbar__tier2">
          <BeamIcon name="filter" size={14} className="mt-filterbar__tier2-icon" />
          <span className="mt-filterbar__tier2-label">
            Within{" "}
            <b>
              {typeCounts.find((t) => t.key === selectedType)?.label ||
                selectedType}
            </b>
            , group by
          </span>
          <Select<SecondarySort>
            className="mt-filterbar__subsort"
            size="small"
            value={selectedSecondarySort}
            onChange={(value) => update({ secondarySort: value })}
            options={SECONDARY_SORTS}
            dropdownMatchSelectWidth={false}
          />
        </div>
      )}

      {suggestedTags.length > 0 && (
        <div className="mt-filterbar__suggested">
          <span>Quick tags:</span>
          {suggestedTags.map((tag) => (
            <button
              key={tag}
              type="button"
              className="mt-filterbar__suggest"
              disabled={state.tags.includes(tag)}
              onClick={() => addTag(tag)}
            >
              #{tag}
            </button>
          ))}
          {hasActiveFilters && (
            <button
              type="button"
              className="mt-filterbar__reset"
              onClick={() =>
                update({ type: "all", tags: [], query: "" })
              }
            >
              Reset filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default GlobalFilterBar;
